/**
 * Table row for the Agents list page.
 *
 * Full-row click target is the agent detail link; the action dropdown
 * stops propagation so menu interaction doesn't navigate.
 */
import { Link, useNavigate } from 'react-router-dom';
import { MoreVertical, Play, RotateCw, Square, Trash2 } from 'lucide-react';
import type { AgentSummary } from '@/api/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { formatRelative } from '@/lib/format';

interface AgentListRowProps {
  agent: AgentSummary;
  onAction: (action: 'start' | 'stop' | 'restart') => void;
  onDelete: () => void;
}

export function AgentListRow({ agent, onAction, onDelete }: AgentListRowProps) {
  const navigate = useNavigate();
  const href = `/agents/${encodeURIComponent(agent.name)}`;

  return (
    <tr
      className="group border-b last:border-b-0 hover:bg-muted/40 cursor-pointer"
      onClick={() => navigate(href)}
    >
      <td className="py-3 px-4">
        <Link
          to={href}
          className="font-medium hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {agent.name}
        </Link>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground max-w-[24rem] truncate">
        {agent.description ?? <span className="italic">No description</span>}
      </td>
      <td className="py-3 px-4">
        <StatusBadge status={agent.status} />
      </td>
      <td className="py-3 px-4 text-sm">
        {agent.discord_enabled ? (
          agent.discord_connected ? (
            <span className="text-success">Connected</span>
          ) : (
            <span className="text-warning">Offline</span>
          )
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">
        {agent.last_message_at ? formatRelative(agent.last_message_at) : '—'}
      </td>
      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Actions for ${agent.name}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction('start')}>
              <Play className="h-4 w-4" />
              Start
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction('restart')}>
              <RotateCw className="h-4 w-4" />
              Restart
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction('stop')}>
              <Square className="h-4 w-4" />
              Stop
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
